/**
 * Application Initializer - Handles complete application setup and initialization
 * Extracted from main.ts to follow Single Responsibility Principle
 * Updated to use standardized error handling
 */

import type { StateManagerInterface, DataManagerInterface, ConfigManagerInterface, AudioEngineInterface, EQExporterInterface, NotificationSystemInterface, UIControllerInterface } from '../types';
import { container, ServiceKeys } from './service-container';
import { ServiceRegistry } from './service-registry';
import { PeaceEQAPI } from './peace-eq-api';
import { Logger } from './logger';
import { getErrorHandler, ServiceError } from './error-handling';

// Import controllers that need special initialization order
import { AppController } from '../features/equalizer/app-controller';
import { EventHandlers } from '../features/equalizer/event-handlers';

const logger = Logger.createComponentLogger('app-initializer');

export class AppInitializer {
    private appController?: AppController;

    /**
     * Initialize the complete Peace EQ application
     */
    public async initialize(): Promise<void> {
        try {
            if (typeof window === 'undefined') {
                logger.warn('Not in browser environment, skipping initialization');
                return;
            }

            logger.info('Starting Peace EQ application initialization...');
            
            await this.initializeServices();
            await this.initializeControllers();
            await this.setupAPI();
            await this.renderInitialUI();
            
            logger.info('Peace EQ initialized successfully');
        } catch (error) {
            const initError = new ServiceError('AppInitializer', 'initialize', 'Failed to initialize Peace EQ application', error instanceof Error ? error : undefined);
            getErrorHandler().handleError(initError, 'AppInitializer', 'initialize');
            throw initError;
        }
    }

    /**
     * Initialize all services using the service registry
     */
    private async initializeServices(): Promise<void> {
        logger.info('Initializing services...');
        
        const serviceRegistry = new ServiceRegistry();
        serviceRegistry.registerAll();
    }

    /**
     * Initialize controllers with proper dependency order
     * AppController must be created before EventHandlers to avoid circular dependency
     */
    private async initializeControllers(): Promise<void> {
        logger.info('Creating AppController...');
        this.appController = new AppController();
        container.register('appController', this.appController);
        
        logger.info('Creating EventHandlers with dependencies...');
        this.createEventHandlers();
        
        // Update controller references
        this.updateControllerReferences();
    }

    /**
     * Create EventHandlers with all required dependencies
     */
    private createEventHandlers(): void {
        const stateManager = container.get<StateManagerInterface>('stateManager');
        const dataManager = container.get<DataManagerInterface>('dataManager');
        const configManager = container.get<ConfigManagerInterface>('configManager');
        const audioEngine = container.get<AudioEngineInterface>('audioEngine');
        const graphRenderer = container.get('graphRenderer') as { render: () => void };
        const eqExporter = container.get<EQExporterInterface>('eqExporter');
        const notificationSystem = container.get<NotificationSystemInterface>('notificationSystem');
        const modalDialog = container.get('modalDialog') as { showConfirmation: (message: string, onConfirm?: () => void, onCancel?: () => void) => void };
        const appConfig = configManager.getConfig();
        
        if (!this.appController) {
            throw new ServiceError('AppInitializer', 'createEventHandlers', 'AppController must be created before EventHandlers');
        }
        
        const eventHandlers = new EventHandlers(
            stateManager, 
            dataManager, 
            configManager, 
            appConfig, 
            audioEngine, 
            graphRenderer, 
            eqExporter, 
            notificationSystem, 
            modalDialog, 
            this.appController
        );
        
        container.register(ServiceKeys.EVENT_HANDLERS, eventHandlers);
    }

    /**
     * Update controller cross-references after all are created
     */
    private updateControllerReferences(): void {
        if (!this.appController) {
            throw new ServiceError('AppInitializer', 'updateControllerReferences', 'AppController not initialized');
        }

        // Update AppController with EventHandlers reference
        const eventHandlers = container.get(ServiceKeys.EVENT_HANDLERS);
        this.appController.setEventHandlers(eventHandlers);
        
        // Update UIController with appController reference
        const uiController = container.get<UIControllerInterface>('uiController');
        uiController.setAppController(this.appController);
    }

    /**
     * Setup the clean public API
     */
    private async setupAPI(): Promise<void> {
        logger.info('Setting up Peace EQ API...');
        
        const peaceEQAPI = new PeaceEQAPI(container);
        
        // Make minimal global access available for debugging and external use
        (window as any).PeaceEQ = peaceEQAPI;
        (window as any).serviceContainer = container; // For debugging only
    }

    /**
     * Render initial UI components
     */
    private async renderInitialUI(): Promise<void> {
        logger.info('Rendering initial graph...');
        
        const peaceEQAPI = (window as any).PeaceEQ as PeaceEQAPI;
        peaceEQAPI.drawGraph();
    }

    /**
     * Get the initialized app controller for external access
     */
    public getAppController(): AppController | undefined {
        return this.appController;
    }
}